import { supabase } from '../supabase/supabaseClient';

// Configuration par défaut
const DEFAULT_FROM = 'noreply@growlyy.fr';
const DEFAULT_REPLY_TO = 'corenthin@growlyy.fr';

// Types pour les emails
export interface EmailData {
  to: string | string[];
  subject: string;
  html: string;
  from?: string;
  replyTo?: string;
  bcc?: string | string[];
}

export interface EmailResult {
  success: boolean;
  id?: string;
  error?: string;
}

/**
 * Service d'envoi d'emails avec Resend via fonction Edge Supabase
 */
export class EmailService {
  /**
   * Envoie un email via la fonction Edge Supabase
   */
  static async sendEmail(emailData: EmailData): Promise<EmailResult> {
    try {
      // Obtenir le token d'authentification
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        return {
          success: false,
          error: 'Non authentifié - veuillez vous connecter'
        };
      }

      // Appeler la fonction Edge
      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          ...emailData,
          from: emailData.from || DEFAULT_FROM,
          replyTo: emailData.replyTo || DEFAULT_REPLY_TO
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Erreur fonction Edge:', error);
        return {
          success: false,
          error: error.message || 'Erreur lors de l\'envoi de l\'email'
        };
      }

      return {
        success: data?.success || false,
        id: data?.id,
        error: data?.error
      };
    } catch (error: any) {
      console.error('Erreur lors de l\'envoi de l\'email:', error);
      return {
        success: false,
        error: error.message || 'Erreur inconnue lors de l\'envoi'
      };
    }
  }

  /**
   * Envoie un email de test
   */
  static async sendTestEmail(): Promise<EmailResult> {
    return this.sendEmail({
      to: 'corenthin@growlyy.fr',
      subject: 'Test Email - Growlyy Dashboard',
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h1 style="color: #1976d2;">Test Email</h1>
          <p>Félicitations ! Votre service d'email Resend fonctionne correctement.</p>
          <p>Cet email a été envoyé depuis le <strong>Dashboard Growlyy</strong>.</p>
          <hr style="margin: 20px 0; border: none; border-top: 1px solid #eee;">
          <p style="color: #666; font-size: 14px;">
            Email envoyé le ${new Date().toLocaleString('fr-FR')}
          </p>
        </div>
      `
    });
  }

  /**
   * Envoie un email de notification de nouveau RDV
   */
  static async sendRdvNotification(leadData: {
    prenom: string;
    nom: string;
    entreprise: string;
    email: string;
    rdvDate: string;
  }): Promise<EmailResult> {
    return this.sendEmail({
      to: 'corenthin@growlyy.fr',
      subject: `Nouveau RDV confirmé - ${leadData.prenom} ${leadData.nom}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h1 style="color: #1976d2;">Nouveau RDV Confirmé</h1>
          
          <div style="background: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h2 style="margin-top: 0; color: #333;">Détails du prospect</h2>
            <p><strong>Nom :</strong> ${leadData.prenom} ${leadData.nom}</p>
            <p><strong>Entreprise :</strong> ${leadData.entreprise}</p>
            <p><strong>Email :</strong> ${leadData.email}</p>
            <p><strong>Date du RDV :</strong> ${new Date(leadData.rdvDate).toLocaleDateString('fr-FR', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</p>
          </div>
          
          <p>Un nouveau rendez-vous a été confirmé dans votre pipeline.</p>
          
          <hr style="margin: 20px 0; border: none; border-top: 1px solid #eee;">
          <p style="color: #666; font-size: 14px;">
            Notification envoyée depuis Growlyy Dashboard
          </p>
        </div>
      `
    });
  }

  /**
   * Envoie un email de rapport quotidien
   */
  static async sendDailyReport(stats: {
    nouveauxRdv: number;
    rdvQualifies: number;
    rdvDisqualifies: number;
    date: string;
  }): Promise<EmailResult> {
    return this.sendEmail({
      to: 'corenthin@growlyy.fr',
      subject: `Rapport quotidien - ${stats.date}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h1 style="color: #1976d2;">Rapport Quotidien</h1>
          <p style="color: #666;">Statistiques du ${stats.date}</p>
          
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 20px; margin: 30px 0;">
            <div style="text-align: center; padding: 20px; background: #e3f2fd; border-radius: 8px;">
              <h3 style="margin: 0; color: #1976d2; font-size: 2em;">${stats.nouveauxRdv}</h3>
              <p style="margin: 5px 0 0 0; color: #1976d2;">Nouveaux RDV</p>
            </div>
            
            <div style="text-align: center; padding: 20px; background: #e8f5e8; border-radius: 8px;">
              <h3 style="margin: 0; color: #2e7d32; font-size: 2em;">${stats.rdvQualifies}</h3>
              <p style="margin: 5px 0 0 0; color: #2e7d32;">RDV Qualifiés</p>
            </div>
            
            <div style="text-align: center; padding: 20px; background: #ffebee; border-radius: 8px;">
              <h3 style="margin: 0; color: #d32f2f; font-size: 2em;">${stats.rdvDisqualifies}</h3>
              <p style="margin: 5px 0 0 0; color: #d32f2f;">RDV Disqualifiés</p>
            </div>
          </div>
          
          <p>Continuez votre excellent travail !</p>
          
          <hr style="margin: 20px 0; border: none; border-top: 1px solid #eee;">
          <p style="color: #666; font-size: 14px;">
            Rapport généré automatiquement par Growlyy Dashboard
          </p>
        </div>
      `
    });
  }

  /**
   * Envoie un email de rappel pour les RDV du jour avec avertissement de facturation
   */
  static async sendRdvReminderToday(rdvData: {
    prenom: string;
    nom: string;
    entreprise: string;
    email: string;
    telephone?: string;
    rdvDate: string;
    rdvTime?: string;
    qualification?: string;
    etatRdv?: string;
  }): Promise<EmailResult> {
    const rdvDateTime = new Date(rdvData.rdvDate);
    const formattedDate = rdvDateTime.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const formattedTime = rdvData.rdvTime || rdvDateTime.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });

    return this.sendEmail({
      to: 'corenthin@growlyy.fr',
      subject: `⏰ RDV Aujourd'hui - ${rdvData.prenom} ${rdvData.nom} (${formattedTime})`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; line-height: 1.6;">
          <!-- Header -->
          <div style="background: linear-gradient(135deg, #1976d2, #1565c0); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
            <h1 style="margin: 0; font-size: 28px;">🗓️ RDV Aujourd'hui</h1>
            <p style="margin: 10px 0 0 0; font-size: 16px; opacity: 0.9;">Rendez-vous Growlyy prévu</p>
          </div>
          
          <!-- Alert facturation -->
          <div style="background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 8px; padding: 20px; margin: 20px 0;">
            <div style="display: flex; align-items: center; margin-bottom: 10px;">
              <span style="font-size: 24px; margin-right: 10px;">⚠️</span>
              <h2 style="margin: 0; color: #856404; font-size: 20px;">Rappel Important</h2>
            </div>
            <p style="margin: 0; color: #856404; font-weight: 500;">
              Ce RDV doit être <strong>qualifié dans les 24 heures</strong> suivant sa tenue, sinon il sera automatiquement facturé.
            </p>
          </div>
          
          <!-- Détails du RDV -->
          <div style="background: #f8f9fa; padding: 25px; border-radius: 8px; margin: 20px 0;">
            <h2 style="margin-top: 0; color: #333; font-size: 22px; border-bottom: 2px solid #1976d2; padding-bottom: 10px;">
              📋 Détails du Rendez-vous
            </h2>
            
            <div style="display: grid; gap: 15px;">
              <div style="display: flex; align-items: center;">
                <span style="font-size: 18px; margin-right: 10px;">👤</span>
                <div>
                  <strong>Prospect :</strong> ${rdvData.prenom} ${rdvData.nom} - ${rdvData.entreprise}
                </div>
              </div>
              
              <div style="display: flex; align-items: center;">
                <span style="font-size: 18px; margin-right: 10px;">📧</span>
                <div>
                  <strong>Email :</strong> ${rdvData.email}
                </div>
              </div>
              
              ${rdvData.telephone ? `
              <div style="display: flex; align-items: center;">
                <span style="font-size: 18px; margin-right: 10px;">📞</span>
                <div>
                  <strong>Téléphone :</strong> ${rdvData.telephone}
                </div>
              </div>
              ` : ''}
              
              <div style="display: flex; align-items: center;">
                <span style="font-size: 18px; margin-right: 10px;">📅</span>
                <div>
                  <strong>Date :</strong> ${formattedDate}
                </div>
              </div>
              
              <div style="display: flex; align-items: center;">
                <span style="font-size: 18px; margin-right: 10px;">⏰</span>
                <div>
                  <strong>Heure :</strong> ${formattedTime}
                </div>
              </div>
              
              ${rdvData.qualification || rdvData.etatRdv ? `
              <div style="display: flex; align-items: center;">
                <span style="font-size: 18px; margin-right: 10px;">📊</span>
                <div>
                  <strong>Qualification :</strong> ${rdvData.qualification || rdvData.etatRdv || 'En attente'}
                </div>
              </div>
              ` : ''}
              
              ${!rdvData.qualification && !rdvData.etatRdv ? `
              <div style="display: flex; align-items: center;">
                <span style="font-size: 18px; margin-right: 10px;">📊</span>
                <div>
                  <strong>Qualification :</strong> En attente
                </div>
              </div>
              ` : ''}
            </div>
          </div>
          
          <!-- Actions -->
          <div style="background: #e3f2fd; padding: 20px; border-radius: 8px; text-align: center; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #1976d2;">🎯 Actions à réaliser</h3>
            <ul style="text-align: left; color: #333; padding-left: 20px;">
              <li style="margin-bottom: 8px;">Effectuer le rendez-vous avec ${rdvData.prenom} ${rdvData.nom}</li>
              <li style="margin-bottom: 8px;">Qualifier le résultat : <strong>Validé</strong>, <strong>Litige</strong> ou <strong>No show</strong></li>
              <li style="margin-bottom: 8px;">Ajouter un commentaire si nécessaire</li>
              <li style="margin-bottom: 8px;"><strong>Important :</strong> Qualification obligatoire sous 24h</li>
            </ul>
          </div>
          
          <!-- CTA Button -->
          <div style="text-align: center; margin: 30px 0;">
            <a href="https://dashboard.growlyy.fr/rdv" 
               style="background: #1976d2; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; display: inline-block;">
              🚀 Aller au Dashboard RDV
            </a>
          </div>
          
          <!-- Footer -->
          <hr style="margin: 30px 0; border: none; border-top: 1px solid #eee;">
          <div style="text-align: center; color: #666; font-size: 14px;">
            <p style="margin: 5px 0;">📱 Notification automatique Growlyy</p>
            <p style="margin: 5px 0;">Envoyé le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
        </div>
      `
    });
  }

  /**
   * Envoie un email de confirmation de désinscription
   */
  static async sendUnsubscribeConfirmation(userData: {
    email: string;
    fullName?: string;
  }): Promise<EmailResult> {
    const today = new Date().toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return this.sendEmail({
      to: userData.email,
      bcc: 'melanie@growlyy.fr', // Copie cachée pour Mélanie
      subject: 'Confirmation de désinscription - Growlyy',
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; line-height: 1.6;">
          <!-- Header -->
          <div style="background: linear-gradient(135deg, #f44336, #d32f2f); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
            <h1 style="margin: 0; font-size: 28px;">✅ Désinscription confirmée</h1>
            <p style="margin: 10px 0 0 0; font-size: 16px; opacity: 0.9;">Votre compte Growlyy a été supprimé</p>
          </div>
          
          <!-- Contenu principal -->
          <div style="background: white; padding: 30px; border-radius: 0 0 10px 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
            <p style="color: #333; margin-bottom: 20px;">
              ${userData.fullName ? `Bonjour ${userData.fullName},` : 'Bonjour,'}
            </p>
            
            <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h2 style="margin-top: 0; color: #d32f2f; font-size: 20px;">📧 Désinscription effectuée</h2>
              <p style="margin-bottom: 15px; color: #555;">
                Nous confirmons que votre compte Growlyy (<strong>${userData.email}</strong>) a été supprimé avec succès le <strong>${today}</strong>.
              </p>
              
              <div style="background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 6px; padding: 15px; margin: 15px 0;">
                <h3 style="margin-top: 0; color: #856404; font-size: 16px;">🗑️ Données supprimées :</h3>
                <ul style="color: #856404; margin: 10px 0; padding-left: 20px;">
                  <li>Tous vos leads et prospects</li>
                  <li>Historique des RDV</li>
                  <li>Campagnes et statistiques</li>
                  <li>Configuration et préférences</li>
                </ul>
              </div>
            </div>
            
            <div style="background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 8px; padding: 20px; margin: 20px 0;">
              <h3 style="margin-top: 0; color: #856404; font-size: 18px;">⚠️ Important : Facturation</h3>
              <p style="color: #856404; margin-bottom: 10px; font-weight: 500;">
                <strong>Les RDV déjà programmés, qualifiés et effectués restent dus et seront facturés</strong> même après la désinscription de votre compte.
              </p>
              <p style="color: #856404; margin: 0; font-size: 14px;">
                Cette désinscription n'annule pas les prestations déjà réalisées conformément à nos conditions générales de vente.
              </p>
            </div>
            
            <div style="background: #e8f5e8; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h3 style="margin-top: 0; color: #2e7d32;">💚 Merci pour votre confiance</h3>
              <p style="color: #2e7d32; margin-bottom: 15px;">
                Nous vous remercions d'avoir utilisé Growlyy. Nous espérons que notre solution vous a aidé dans vos prospections.
              </p>
              
              <p style="color: #2e7d32; margin-bottom: 10px;">
                <strong>Vous changez d'avis ?</strong> Vous pourrez créer un nouveau compte à tout moment sur <a href="https://growlyy.fr" style="color: #1976d2;">growlyy.fr</a>
              </p>
            </div>
            
            <div style="background: #e3f2fd; padding: 20px; border-radius: 8px; margin: 20px 0;">
              <h3 style="margin-top: 0; color: #1976d2;">📞 Support</h3>
              <p style="color: #1976d2; margin-bottom: 10px;">
                Si vous avez des questions ou souhaitez nous faire part de vos commentaires, n'hésitez pas à nous contacter :
              </p>
              <p style="color: #1976d2; margin: 5px 0;">
                📧 Email : <a href="mailto:support@growlyy.fr" style="color: #1976d2;">support@growlyy.fr</a>
              </p>
            </div>
            
            <div style="text-align: center; margin: 30px 0;">
              <p style="color: #666; font-size: 14px; margin: 0;">
                À bientôt peut-être ! 👋
              </p>
            </div>
          </div>
          
          <!-- Footer -->
          <div style="text-align: center; color: #666; font-size: 12px; margin-top: 20px; padding: 20px;">
            <p style="margin: 5px 0;">Growlyy - Solution de prospection B2B</p>
            <p style="margin: 5px 0;">Email automatique envoyé le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
        </div>
      `
    });
  }
}

export default EmailService; 